import { onCLS, onLCP, onINP, } from "web-vitals/attribution";
// Deep imports, not the `restaumatic-client` barrel: this loads on every page via app.js.
import { FieldTestsStorageManager } from "restaumatic-client/src/storageBase/FieldTestsStorageManager";
import { PerformanceMonitorStorageManager } from "restaumatic-client/src/storageBase/PerformanceMonitorStorageManager";
import { Tracking } from "restaumatic-client/src/tracking/Tracking";
import { weightedRandom } from "restaumatic-client/src/fieldTests/weightedRandom";
import Device from "./Device";
import { getHardwareInfo, getNetworkInfo } from "./Capabilities";
// Load time in milliseconds
function getLoadTime() {
    // Modern Navigation Timing API
    const [nav] = performance.getEntriesByType("navigation");
    if (nav && nav.loadEventEnd > 0) {
        return nav.loadEventEnd;
    }
    // Legacy fallback
    const timing = performance.timing;
    if (timing.loadEventEnd > 0) {
        return timing.loadEventEnd - timing.navigationStart;
    }
    return 0; // still loading
}
function getData(onComplete) {
    var _a;
    const vitals = {
        LCP: {
            value: null,
            quality: null,
            navigationType: null,
            debugTarget: undefined,
            ttfb: null,
            resourceLoadDelay: null,
            resourceLoadDuration: null,
            elementRenderDelay: null,
        },
        CLS: {
            value: null,
            quality: null,
            navigationType: null,
            debugTarget: undefined,
            loadState: null,
            largestShiftTime: null,
            largestShiftValue: null,
        },
        INP: {
            value: null,
            quality: null,
            navigationType: null,
            debugTarget: undefined,
            loadState: null,
            interactionType: null,
            inputDelay: null,
            totalPaintDuration: null,
            totalScriptDuration: null,
            totalStyleAndLayoutDuration: null,
        },
    };
    // Vitals Info
    function handleMetric(metric) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        let target;
        switch (metric.name) {
            case "CLS":
                target = metric.attribution.largestShiftTarget;
                vitals.CLS = {
                    value: metric.value,
                    quality: metric.rating,
                    navigationType: metric.navigationType,
                    debugTarget: target,
                    loadState: (_a = metric.attribution.loadState) !== null && _a !== void 0 ? _a : null,
                    largestShiftTime: (_b = metric.attribution.largestShiftTime) !== null && _b !== void 0 ? _b : null,
                    largestShiftValue: (_c = metric.attribution.largestShiftValue) !== null && _c !== void 0 ? _c : null,
                };
                break;
            case "INP":
                target = metric.attribution.interactionTarget;
                vitals.INP = {
                    value: metric.value,
                    quality: metric.rating,
                    navigationType: metric.navigationType,
                    debugTarget: target,
                    loadState: (_d = metric.attribution.loadState) !== null && _d !== void 0 ? _d : null,
                    interactionType: (_e = metric.attribution.interactionType) !== null && _e !== void 0 ? _e : null,
                    inputDelay: (_f = metric.attribution.inputDelay) !== null && _f !== void 0 ? _f : null,
                    totalPaintDuration: (_g = metric.attribution.totalPaintDuration) !== null && _g !== void 0 ? _g : null,
                    totalScriptDuration: (_h = metric.attribution.totalScriptDuration) !== null && _h !== void 0 ? _h : null,
                    totalStyleAndLayoutDuration: (_j = metric.attribution
                        .totalStyleAndLayoutDuration) !== null && _j !== void 0 ? _j : null,
                };
                break;
            case "LCP":
                target = metric.attribution.target;
                vitals.LCP = {
                    value: metric.value,
                    quality: metric.rating,
                    navigationType: metric.navigationType,
                    debugTarget: target,
                    ttfb: (_k = metric.attribution.timeToFirstByte) !== null && _k !== void 0 ? _k : null,
                    resourceLoadDelay: (_l = metric.attribution.resourceLoadDelay) !== null && _l !== void 0 ? _l : null,
                    resourceLoadDuration: (_m = metric.attribution.resourceLoadDuration) !== null && _m !== void 0 ? _m : null,
                    elementRenderDelay: (_o = metric.attribution.elementRenderDelay) !== null && _o !== void 0 ? _o : null,
                };
                break;
        }
    }
    onCLS(handleMetric);
    onLCP(handleMetric);
    onINP(handleMetric);
    // Site Info
    const site = {
        id: Skubacz.configuration.tracking.siteId,
        name: Skubacz.configuration.tracking.siteName,
        url: window.location.origin,
        themeName: Skubacz.configuration.tracking.themeName,
        viewName: Skubacz.configuration.tracking.viewName,
        restaurantSlug: Skubacz.configuration.restaurant_slug,
        prefetchMenuOnHomepage: !!Skubacz.configuration.prefetch_menu_on_homepage,
        fieldTestExperiments: (_a = FieldTestsStorageManager.restore()) !== null && _a !== void 0 ? _a : {},
    };
    // Device info
    const { cores, memoryGB } = getHardwareInfo();
    const hardware = { cores, memoryGB };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const screen = { width: window.screen.width, height: window.screen.height };
    // Network info
    const { connectionType, effectiveType } = getNetworkInfo();
    const network = {
        connectionType,
        qualityType: effectiveType,
    };
    function report() {
        if (document.visibilityState === "hidden") {
            const data = {
                loadTime: getLoadTime(),
                vitals,
                site,
                device: {
                    type: Device.getDeviceType(),
                    browser: Device.getBrowserName(),
                    os: Device.getOSName(),
                    hardware,
                    viewport,
                    screen,
                },
                network,
            };
            onComplete(data);
        }
    }
    document.addEventListener("visibilitychange", report, { once: true });
}
function checkIsEnabled() {
    // 1% of users have performance monitoring enabled, saved in local storage
    let storageData = PerformanceMonitorStorageManager.restore();
    if (storageData === null ||
        // NOTICE: dumb check for date change, it is enough for our purpose to assign new value once per day
        !storageData.date.startsWith(new Date().toISOString().slice(0, 10))) {
        const leverPullResult = weightedRandom([
            ["Disabled", 99],
            ["Enabled", 1],
        ]);
        storageData = {
            enabled: leverPullResult === "Enabled",
            date: new Date().toISOString(),
        };
        PerformanceMonitorStorageManager.store(storageData);
    }
    if (!storageData.enabled) {
        return false;
    }
    // Only for ordering process pages
    const viewName = Skubacz.configuration.view_name;
    const isValidView = [
        "home",
        "restaurants",
        "menu",
        "checkout",
        "summary",
    ].includes(viewName);
    if (!isValidView) {
        return false;
    }
    // Disable for AM iframe due to lack of support of web-vitals library
    const isAmInIframe = Device.isInIframe() && Skubacz.configuration.theme_name === "active_menu";
    if (isAmInIframe) {
        return false;
    }
    return true;
}
export default {
    init: () => {
        const isEnabled = checkIsEnabled();
        if (!isEnabled) {
            return;
        }
        getData((data) => {
            Tracking.trackEvent({
                tag: "PerformanceMonitorData",
                contents: { data },
            });
        });
    },
};
